import { createPopper } from '@popperjs/core';
import AirDatepicker from 'air-datepicker';
import localeEn from 'air-datepicker/locale/en';
import localeRu from 'air-datepicker/locale/ru';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as ArrowDown } from '../../assets/svg/ArrowDown.svg';
import { ReactComponent as Close } from '../../assets/svg/Close.svg';
import { ReactComponent as Calendar } from '../../assets/svg/calendar.svg';
import { useLanguage, useOutsideAlerter } from '../../hooks';


interface DatePickerProps {
    placeholder?: string,
    value?: Date,
    fullSize?: boolean,
    simple?: boolean,
    disabled?: boolean,
    setValue?: (value?: Date) => void,
    className?: string,
    minDate?: Date,
    maxDate?: Date,
    handleEdit?: boolean,
    timepicker?: boolean,
    closeOnActivate?: boolean,
    error?: string,
    range?: boolean,
    rangeValue?: Date[],
    onChangeRangeValue?: (v: Date[]) => void
}

export function DatePicker({
    value, handleEdit, disabled, placeholder,
    fullSize, simple, setValue, error,
    range, rangeValue, onChangeRangeValue, className,
    minDate, maxDate, timepicker, closeOnActivate,
}: DatePickerProps) {
    const refWrapper = useRef<HTMLDivElement | null>(null);
    const refDiv = useRef<HTMLDivElement | null>(null);
    const lang = useLanguage();
    const [selected, setSelected] = useState<boolean>(false);
    const [active, setActive] = useState<boolean>(false);
    const [label, setLabel] = useState(placeholder);
    const [textValue, setTextValue] = useState('');

    useOutsideAlerter(refDiv, setActive, refWrapper);

    const trySetValue = useCallback(() => {
        if (setValue && !active) {
            if (textValue) {
                const match = textValue.match(/^(\d{2})\.(\d{2})\.(\d{4})[ ]?(?:[ T](\d{2}):(\d{2})?)?$/);
                // const match = textValue.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
                if (match) {
                    const [_, d, m, y, hh, mm] = match;
                    const nd = Number(d);
                    const nm = Number(m) - 1;
                    const ny = Number(y);
                    if (nd <= 31 && nm < 12) {
                        if (timepicker) {
                            const nhh = hh ? Number(hh) : 0;
                            const nmm = mm ? Number(mm) : 0;
                            if (nhh < 24 && nmm < 60) {
                                const date = new Date(ny, nm, nd, nhh, nmm);
                                setValue(date);
                            } else {
                                setValue(undefined);
                                setTextValue('');
                            }
                        } else {
                            const date = new Date(ny, nm, nd);
                            setValue(date);
                        }
                        setLabel(_);
                    } else {
                        setValue(undefined);
                        setTextValue('');
                    }
                } else {
                    setValue(undefined);
                    setTextValue('');
                }
            } else {
                setValue(undefined);
                setTextValue('');
            }
        }
    }, [textValue, setValue, setTextValue, timepicker, active]);

    const dp = useRef<AirDatepicker<HTMLDivElement> | null>(null);

    const calendarMonths = useMemo(() => lang === 'ru' ? [
        'января',
        'февраля',
        'марта',
        'апреля',
        'мая',
        'июня',
        'июля',
        'августа',
        'сентября',
        'октября',
        'ноября',
        'декабря',
    ] : [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ], [lang]);

    const onSelect = useCallback(({ date }: {
        date: Date | Date[];
        formattedDate: string | string[];
        datepicker: AirDatepicker<HTMLDivElement>;
    }) => {
        const d = date as Date;
        setValue && setValue(d);

    }, [calendarMonths, placeholder, simple, setValue])

    // Start init
    useEffect(() => {
        // Save instance for the further update
        if (refDiv.current && refWrapper.current) {
            if (!dp.current) {
                createPopper(refWrapper.current, refDiv.current, {
                    placement: 'bottom-start',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 4],
                            },
                        },
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['top-start', 'top-end'],
                            },
                        },
                    ],
                });
                dp.current = new AirDatepicker(refDiv.current, {
                    minDate: minDate,
                    timepicker,
                    minutesStep: 5,
                    locale: lang === 'ru' ? localeRu : localeEn,
                    maxDate: maxDate,
                    onSelect: onSelect,
                    selectedDates: value ? (range ? rangeValue : [value]) : false,
                    range: range
                    // onRenderCell: ({ date, cellType }) => {
                    //     const isDay = cellType === 'day';
                    //     const shouldChangeContent = isDay && date.getTime() === new Date(2025, 10, 17).getTime();
                    //     const randomEmoji = '🌐';

                    //     return {
                    //         html: shouldChangeContent ? randomEmoji : undefined,
                    //         classes: shouldChangeContent ? '-emoji-cell-' : undefined,
                    //         attrs: {
                    //             title: shouldChangeContent ? randomEmoji : ''
                    //         }
                    //     }
                    // }
                });
            }
        }
    }, [onSelect, active]);

    useEffect(() => {
        if (dp.current) {
            if (active) {
                dp.current.show();
            } else {
                dp.current = null;
            }
        }
    }, [active]);

    useEffect(() => {
        if (!value) {
            setLabel(placeholder);
            setSelected(false);
        } else if (range) {
            const dates = value as unknown as Date[];
            let textDate = '';
            dates.forEach((v) => {
                textDate += ('0' + v.getDate()).slice(-2);
                textDate += '.';
                textDate += ('0' + (v.getMonth() + 1)).slice(-2);
                textDate += '.';
                textDate += v.getFullYear();
                if (timepicker) {
                    textDate += ' ';
                    textDate += ('0' + v.getHours()).slice(-2);
                    textDate += ':';
                    textDate += ('0' + v.getMinutes()).slice(-2);
                }
            });

            setTextValue(textDate);
            setLabel(textDate);
            setSelected(true);

            onChangeRangeValue && onChangeRangeValue(dates);
        } else if (typeof (value) === 'object') {
            // dp.current?.selectDate(value);
            // setValue && setValue(value);
            let textDate = '';
            if (simple) {
                textDate += ('0' + value.getDate()).slice(-2);
                textDate += '.';
                textDate += ('0' + (value.getMonth() + 1)).slice(-2);
                textDate += '.';
                textDate += value.getFullYear();
                if (timepicker) {
                    textDate += ' ';
                    textDate += ('0' + value.getHours()).slice(-2);
                    textDate += ':';
                    textDate += ('0' + value.getMinutes()).slice(-2);
                }
            } else {
                textDate += value.getDate();
                textDate += ' ';
                textDate += calendarMonths[value.getMonth()];
                textDate += ' ';
                textDate += value.getFullYear();
                if (timepicker) {
                    textDate += ' ';
                    textDate += ('0' + value.getHours()).slice(-2);
                    textDate += ':';
                    textDate += ('0' + value.getMinutes()).slice(-2);
                }
            }
            setTextValue(textDate);
            setLabel(textDate);
            setSelected(true);
            closeOnActivate && setActive(false);
        }
        // eslint-disable-next-line
    }, [value, timepicker]);

    return (
        <>
            <div
                ref={refWrapper}
                className={'form__calendar ' + (className || '') + (simple ? ' _simple' : '') + (error ? ' _error' : '') + (fullSize ? '' : ' _min') + (selected ? ' _selected' : '') + (active ? ' _active' : '')}
                onClick={(e) => { e.stopPropagation(); (!disabled) && setActive(!active); }}
                style={{ minHeight: fullSize ? undefined : '28px' }}
            >
                {!simple && <Calendar className="form__calendar-icon" />}
                {handleEdit ?
                    <input className={'form__input' + (error ? ' _error' : '')} type="text" placeholder={placeholder} value={textValue} style={{ border: '0px', padding: 0, width: '100%' }} onChange={(e) => setTextValue(e.target.value)} onBlur={trySetValue} />
                    :
                    <>
                        <span className="form__calendar-label">{label}</span>
                        <input type="hidden" value="" className="form__calendar-input" />
                    </>
                }
                <ArrowDown className="form__calendar-arrow" />
                <div className="form__calendar-remove" onClick={(e) => {
                    e.stopPropagation();
                    setValue && setValue(undefined);
                    dp.current?.clear();
                    setSelected(false);
                    setActive(false);
                    setLabel(placeholder);
                }}>
                    <Close />
                </div>
            </div>
            {error && <span className="form__input-error-message">{error}</span>}
            {active && ReactDOM.createPortal(
                <div
                    ref={refDiv}
                    className="form__calendar-body"
                    style={{
                        display: 'block',
                        position: 'absolute',
                        inset: '0px auto auto 0px',
                        margin: '0px',
                        transform: 'translate(20px, 136px)',
                        zIndex: 50,
                    }}
                    onClick={(e) => e.stopPropagation()}
                ></div>, document.body)}
        </>
    );
}